import React, { useEffect } from 'react'
import { User } from '@/types/auth'
import { cn } from '@/utils/cn'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchAccessLimits } from '@/store/slices/subscriptionSlice'

interface MonthlyLimitsDisplayProps {
    user: User | null
    className?: string
}

export const MonthlyLimitsDisplay: React.FC<MonthlyLimitsDisplayProps> = ({
    user,
    className,
}) => {
    const dispatch = useAppDispatch()
    const { accessLimits, isAccessLimitsLoading, accessLimitsFetchedAt } = useAppSelector(state => state.subscription)

    useEffect(() => {
        if (!user) {
            return
        }

        const fetchedAt = accessLimitsFetchedAt ? new Date(accessLimitsFetchedAt).getTime() : 0
        const isStale = Date.now() - fetchedAt > 5 * 60 * 1000

        if (!accessLimits || isStale) {
            dispatch(fetchAccessLimits())
        }
    }, [dispatch, user, accessLimits, accessLimitsFetchedAt])

    if (!user || isAccessLimitsLoading || !accessLimits) {
        return null
    }

    const gigsPercentage = accessLimits.gigs.unlimited
        ? 0
        : Math.min((accessLimits.gigs.current / Math.max(accessLimits.gigs.limit, 1)) * 100, 100)
    const appsPercentage = accessLimits.applications.unlimited
        ? 0
        : Math.min((accessLimits.applications.current / Math.max(accessLimits.applications.limit, 1)) * 100, 100)

    const isNearLimit = (percentage: number) => percentage >= 80
    const isAtLimit = (percentage: number) => percentage >= 100

    const isEmployer = user.role === 'employer'
    const isWorker = user.role === 'worker'

    return (
        <div className={cn('space-y-4', className)}>
            {isEmployer && (
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-4">
                    <div className="mb-2 flex items-start justify-between">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            Monthly Gigs
                        </h3>
                        <span className={cn(
                            'text-sm font-medium',
                            isAtLimit(gigsPercentage)
                                ? 'text-red-600 dark:text-red-400'
                                : isNearLimit(gigsPercentage)
                                    ? 'text-yellow-600 dark:text-yellow-400'
                                    : 'text-gray-600 dark:text-gray-400'
                        )}>
                            {accessLimits.gigs.current} / {accessLimits.gigs.unlimited ? '∞' : accessLimits.gigs.limit}
                        </span>
                    </div>

                    <progress
                        value={Math.min(gigsPercentage, 100)}
                        max={100}
                        className={cn(
                            'h-2 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-gray-200 dark:[&::-webkit-progress-bar]:bg-gray-700',
                            isAtLimit(gigsPercentage)
                                ? '[&::-webkit-progress-value]:bg-red-500 [&::-moz-progress-bar]:bg-red-500'
                                : isNearLimit(gigsPercentage)
                                    ? '[&::-webkit-progress-value]:bg-yellow-500 [&::-moz-progress-bar]:bg-yellow-500'
                                    : '[&::-webkit-progress-value]:bg-green-500 [&::-moz-progress-bar]:bg-green-500'
                        )}
                    />

                    {isNearLimit(gigsPercentage) && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                            {isAtLimit(gigsPercentage)
                                ? '⚠️ Limit reached. Upgrade your plan for more gigs.'
                                : `⚠️ ${Math.max(accessLimits.gigs.remaining, 0)} ${accessLimits.gigs.remaining === 1 ? 'gig' : 'gigs'} remaining.`}
                        </p>
                    )}
                </div>
            )}

            {isWorker && (
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-4">
                    <div className="mb-2 flex items-start justify-between">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            Monthly Applications
                        </h3>
                        <span className={cn(
                            'text-sm font-medium',
                            isAtLimit(appsPercentage)
                                ? 'text-red-600 dark:text-red-400'
                                : isNearLimit(appsPercentage)
                                    ? 'text-yellow-600 dark:text-yellow-400'
                                    : 'text-gray-600 dark:text-gray-400'
                        )}>
                            {accessLimits.applications.current} / {accessLimits.applications.unlimited ? '∞' : accessLimits.applications.limit}
                        </span>
                    </div>

                    <progress
                        value={Math.min(appsPercentage, 100)}
                        max={100}
                        className={cn(
                            'h-2 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-gray-200 dark:[&::-webkit-progress-bar]:bg-gray-700',
                            isAtLimit(appsPercentage)
                                ? '[&::-webkit-progress-value]:bg-red-500 [&::-moz-progress-bar]:bg-red-500'
                                : isNearLimit(appsPercentage)
                                    ? '[&::-webkit-progress-value]:bg-yellow-500 [&::-moz-progress-bar]:bg-yellow-500'
                                    : '[&::-webkit-progress-value]:bg-green-500 [&::-moz-progress-bar]:bg-green-500'
                        )}
                    />

                    {isNearLimit(appsPercentage) && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                            {isAtLimit(appsPercentage)
                                ? '⚠️ Limit reached. Upgrade your plan for more applications.'
                                : `⚠️ ${Math.max(accessLimits.applications.remaining, 0)} application${accessLimits.applications.remaining === 1 ? '' : 's'} remaining.`}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}
